import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { useForm } from 'react-hook-form';
import _ from 'lodash';
import moment from 'moment';
import { Common, PopUpBox } from '../../components';
import Edit from '../../assets/images/edit.svg';
import Select from 'react-select';
import {
  styles,
  options,
  numberWithNoCommas,
  detectMob,
  pad,
  capitalizeName,
  alphabetOnly,
  alphabetAndNumberOnly,
  normalizeRefRecommenderNo,
  isValidRefRecommenderNo,
  companyNameOnly,
  phoneNumberOnly,
  isOptionalHongKongPhone,
  isOptionalMaxLength,
  numberWithCommas,
  renderRange,
  OPTIONAL_EMAIL_PATTERN,
  emailAddressOnly,
  RESI_OWNER_OPTIONS,
} from '../../utils/common';

import { actionSubmitForm1, actionSubmitForm21, actionSubmitForm22, actionConfirmData, actionApplyOnly } from '../../store/application/actions';
import { actionAddressConfig, actionTracking } from '../../store/common/actions';
import validid from 'validid';
import { actionLendelaAppInfo } from '../../store/lendela/action';
import {
  buildConfirmRouteState,
  buildConsentRouteState,
  buildForm22RouteState,
  clearApplicationDraft,
  createApplicationFlowMeta,
  getApplicationDraft,
  saveApplicationDraft,
} from '../../utils/applicationDraft';
import { APPLICATION_FLOW_STEP, buildApplicationFlowPreviousRoute, canMoveApplicationFlowHistoryBack, getApplicationFlowHistoryIndex, isApplicationFlowBackPopState } from '../../utils/applicationFlowBackNavigation';

const FORM1_MERGE_SOURCE = 'applicationForm1Merge';

const PAY_DAY_OPTIONS = Array.from({ length: 31 }, (_, index) => {
  const value = String(index + 1);
  return { value, label: value };
});

const DATE_YEAR_OPTIONS = renderRange(moment().subtract(60, 'years').format('YYYY'), moment().format('YYYY'), true);
const DATE_MONTH_OPTIONS = renderRange(1, 12, false);
const DATE_DAY_OPTIONS = renderRange(1, 31, false);

const getDateMonthOptions = ({ selectedYear, ageLimit = false, limitToToday = false, restrictBoundaryOptions = true }) => {
  const year = parseInt(selectedYear, 10);

  if (!year) {
    return DATE_MONTH_OPTIONS;
  }

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;

  if (limitToToday && year === currentYear) {
    return DATE_MONTH_OPTIONS.filter((option) => parseInt(option.value, 10) <= currentMonth);
  }

  if (restrictBoundaryOptions && ageLimit && year === currentYear - 18) {
    return DATE_MONTH_OPTIONS.filter((option) => parseInt(option.value, 10) <= currentMonth);
  }

  if (restrictBoundaryOptions && ageLimit && Math.abs(currentYear - year) === 60) {
    return DATE_MONTH_OPTIONS.filter((option) => parseInt(option.value, 10) >= currentMonth);
  }

  return DATE_MONTH_OPTIONS;
};

const getDateDayOptions = ({ selectedYear, selectedMonth, ageLimit = false, limitToToday = false, restrictBoundaryOptions = true }) => {
  const year = parseInt(selectedYear, 10);
  const month = parseInt(selectedMonth, 10);

  if (!year || !month) {
    return DATE_DAY_OPTIONS;
  }

  const today = new Date();
  const currentYear = today.getFullYear();
  const currentMonth = today.getMonth() + 1;
  const currentDay = today.getDate();

  if (limitToToday && year === currentYear && month === currentMonth) {
    return DATE_DAY_OPTIONS.filter((option) => parseInt(option.value, 10) <= currentDay);
  }

  if (restrictBoundaryOptions && ageLimit && year === currentYear - 18 && month === currentMonth) {
    return DATE_DAY_OPTIONS.filter((option) => parseInt(option.value, 10) <= currentDay);
  }

  if (restrictBoundaryOptions && ageLimit && Math.abs(currentYear - year) === 60 && month === currentMonth) {
    return DATE_DAY_OPTIONS.filter((option) => parseInt(option.value, 10) > currentDay);
  }

  return DATE_DAY_OPTIONS;
};

const resolveDateFieldValue = (watchedValue, fallbackValue) => {
  if (watchedValue === undefined) {
    return fallbackValue;
  }

  return watchedValue ?? '';
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const formatDisplayDate = (dateValue) => {
  const normalizedDate = dateValue ? `${dateValue}`.replace(/[-/]/g, '') : '';

  if (normalizedDate.length !== 8) {
    return dateValue ?? '';
  }

  return moment(normalizedDate, 'YYYYMMDD').format('DD/MM/YYYY');
};

const parseAgreeDoc = (agreeDoc) => {
  if (!agreeDoc) {
    return [];
  }

  if (typeof agreeDoc !== 'string') {
    return agreeDoc;
  }

  try {
    return JSON.parse(agreeDoc);
  } catch (error) {
    return [];
  }
};

const toOptionalInteger = (value) => {
  const cleanedValue = numberWithNoCommas(value);
  return cleanedValue ? parseInt(cleanedValue, 10) : undefined;
};

const getDateParts = (dateValue) => {
  const normalizedDate = dateValue ? `${dateValue}`.replace(/[-/]/g, '') : '';

  if (normalizedDate.length !== 8) {
    return { year: '', month: '', day: '' };
  }

  return {
    year: normalizedDate.substring(0, 4),
    month: String(parseInt(normalizedDate.substring(4, 6), 10)),
    day: String(parseInt(normalizedDate.substring(6, 8), 10)),
  };
};

const appendOptionalPayload = (target, payload) => {
  Object.entries(payload).forEach(([key, value]) => {
    if (hasValue(value)) {
      target[key] = value;
    }
  });
};

const buildAddressLine1 = (room, floor, block) => {
  let addrLine1 = '';

  if (room) {
    addrLine1 = `RM ${(room ?? '')
      .replace(new RegExp('ROOM', 'g'), '')
      .replace(new RegExp('RM', 'g'), '')
      .replace(new RegExp('FLAT', 'g'), '')
      .replace(new RegExp('FLT', 'g'), '')
      .replace(new RegExp('UNIT', 'g'), '')
      .trim()}`;
  }

  if (floor) {
    addrLine1 += ` ${(floor ?? '')
      .replace(new RegExp('FLOOR', 'g'), '')
      .replace(new RegExp('F', 'g'), '')
      .replace(new RegExp('/F', 'g'), '')
      .trim()}/F`;
  }

  if (block) {
    addrLine1 += ` BLK ${(block ?? '')
      .replace(new RegExp('BLK', 'g'), '')
      .replace(new RegExp('BLOCK', 'g'), '')
      .replace(new RegExp('BL', 'g'), '')
      .trim()}`;
  }

  return addrLine1.trim();
};

const formatDateParts = (year, month, day) => {
  return year && month && day ? `${year}${pad(month)}${pad(day)}` : '';
};

const formatHkid = (hkid) => {
  if (!hkid) {
    return '';
  }

  const hkidChars = `${hkid}`.split('');
  return `${_.dropRight(hkidChars).join('')}(${_.last(hkidChars)})`;
};

const getHkidParts = (form1 = {}) => {
  if (form1?.hkid_array?.length === 3) {
    return form1.hkid_array;
  }

  const hkid = `${form1?.hkid ?? ''}`;

  return [
    hkid?.[0] ?? '',
    hkid?.slice?.(1, -1) ?? '',
    hkid?.[hkid.length - 1] ?? '',
  ];
};


function ApplicationConfirmData() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const applicationDraft = getApplicationDraft();
  const flowMeta = createApplicationFlowMeta({
    phone: location.state?.form1?.phone_number || applicationDraft?.meta?.phone,
    lendelaAppId: applicationDraft?.meta?.lendelaAppId,
    noDocLoanData: applicationDraft?.meta?.noDocLoanData,
  });
  const matchedDraft = flowMeta?.phone ? getApplicationDraft(flowMeta) : applicationDraft;
  const getLatestMatchedDraft = () => {
    if (!flowMeta?.phone) {
      return applicationDraft;
    }

    return getApplicationDraft(flowMeta) ?? applicationDraft;
  };
  const restoredMergedState = matchedDraft?.form1?.application_form1_merged
    ? {
      status: true,
      form1: matchedDraft.form1,
      source: FORM1_MERGE_SOURCE,
    }
    : null;
  const restoredState = !location.state?.form1
    ? (restoredMergedState ?? buildConfirmRouteState(matchedDraft))
    : null;
  const pageState = location.state ?? restoredState;
  const currentForm1 = pageState?.form1 ?? matchedDraft?.form1;
  const currentForm2 = pageState?.form2 ?? matchedDraft?.form21;
  const currentForm3 = pageState?.form3 ?? matchedDraft?.form22;
  const isForm1MergedFlow = pageState?.source === FORM1_MERGE_SOURCE || currentForm1?.application_form1_merged || currentForm2?.application_form1_merged || currentForm3?.application_form1_merged;
  const {
    setValue,
    getValues,
    watch,
    register,
    handleSubmit,
    setError,
    formState: { errors, isSubmitting },
  } = useForm();
  const [isEditingForm1, setIsEditingForm1] = useState(false);
  const [isEditingForm2, setIsEditingForm2] = useState(false);
  const [isEditingForm3, setIsEditingForm3] = useState(false);
  const [isEditingForm11, setIsEditingForm11] = useState(false);
  const [isEditingMergedForm, setIsEditingMergedForm] = useState(false);
  const [isVisibleInvalidToken, setIsVisibleInvalidToken] = useState(false);
  const [optionsDistrict, setOptionsDistrict] = useState([]);
  const [mergedDistrictOptions, setMergedDistrictOptions] = useState([]);
  const [mergedWorkDistrictOptions, setMergedWorkDistrictOptions] = useState([]);
  const [gender, setGender] = useState(Number(currentForm1?.cust_gender));
  const [errorMessage, setErrorMessage] = useState(null);

  const [lendelaData, setLendelaData] = useState(null);
  const skipPersistOnUnmountRef = useRef(false);
  const persistOnUnmountRef = useRef(null);

  const getDistrictOptionsByArea = (area, addressConfig = currentForm3?.address_config) => {
    switch (area) {
      case 'HK':
        return addressConfig?.district_hk ?? [];
      case 'KLN':
        return addressConfig?.district_kw ?? [];
      case 'NT':
        return addressConfig?.district_nt ?? [];
      default:
        return [];
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
    dispatch(
      actionTracking({
        url: window.location.href,
      }),
    );
  }, [dispatch]);

  // useEffect(() => {
  //   if (!location.state?.status) {
  //     navigate('/');
  //   }
  // }, [location.state?.status, navigate]);

  useEffect(() => {
    const hasMergedConfirmState = !!location.state?.form1?.application_form1_merged || location.state?.source === FORM1_MERGE_SOURCE;
    const hasLegacyConfirmState = !!location.state?.form1 && !!location.state?.form2 && !!location.state?.form3;

    if (!hasMergedConfirmState && !hasLegacyConfirmState) {
      if (restoredState?.form1 && (restoredState?.source === FORM1_MERGE_SOURCE || (restoredState?.form2 && restoredState?.form3))) {
        navigate('/application-confirm-data', {
          replace: true,
          state: restoredState,
        });
        return;
      }

      if (matchedDraft?.form1 && matchedDraft?.form21) {
        navigate('/application-form-2-2', {
          replace: true,
          state: buildForm22RouteState(matchedDraft),
        });
        return;
      }

      if (flowMeta?.phone) {
        navigate('/application-form-1', {
          replace: true,
          state: buildConsentRouteState(flowMeta),
        });
        return;
      }

      navigate('/');
      return;
    }

    const latestDraft = getLatestMatchedDraft();

    saveApplicationDraft({
      meta: flowMeta,
      currentStep: 'confirm',
      form1: latestDraft?.form1 ?? currentForm1,
      form21: isForm1MergedFlow ? null : (latestDraft?.form21 ?? currentForm2),
      form22: isForm1MergedFlow ? null : (latestDraft?.form22 ?? currentForm3),
    });
  }, [flowMeta?.lendelaAppId, flowMeta?.phone, isForm1MergedFlow, location.state?.form1, location.state?.form2, location.state?.form3, location.state?.source, navigate, restoredState?.form1, restoredState?.form2, restoredState?.form3, restoredState?.source]);

  useEffect(() => {
    if (pageState?.lendela_data) {
      setLendelaData(pageState?.lendela_data);
      return;
    }

    if (!flowMeta?.lendelaAppId) {
      return;
    }

    async function getLendelaInfo() {
      const response = await dispatch(actionLendelaAppInfo(flowMeta.lendelaAppId));
      if (response?.sts_code === 0) {
        setLendelaData(response.content);
      }
    }

    getLendelaInfo();
  }, [dispatch, flowMeta?.lendelaAppId, pageState?.lendela_data]);

  useEffect(() => {
    if (currentForm3?.district_value && currentForm3?.area && currentForm3?.address_config) {
      setOptionsDistrict(getDistrictOptionsByArea(currentForm3?.area, currentForm3?.address_config));
      setValue('district', currentForm3?.district_value);
    }
  }, [currentForm3?.area, currentForm3?.district_value, matchedDraft?.updatedAt, setValue]);

  useEffect(() => {
    if (currentForm1?.hkid_array) {
      const hdids = currentForm1?.hkid_array;
      setValue('hkid1', hdids[0])
      setValue('hkid2', hdids[1])
      setValue('hkid3', hdids[2])
    }
    if (Number(currentForm1?.cust_gender) !== gender) {
      setGender(Number(currentForm1?.cust_gender))
    }
    setValue('gender', Number(currentForm1?.cust_gender))
    setValue('ref_recommender_no', currentForm1?.ref_recommender_no ?? '')
  }, [
    currentForm1?.cust_gender,
    currentForm1?.hkid_array?.[0],
    currentForm1?.hkid_array?.[1],
    currentForm1?.hkid_array?.[2],
    currentForm1?.ref_recommender_no,
    gender,
    matchedDraft?.updatedAt,
    setValue,
  ])

  useEffect(() => {
    if (!isForm1MergedFlow || !currentForm1) {
      return;
    }

    const currentHkidParts = getHkidParts(currentForm1);
    const dateOfBirthParts = getDateParts(currentForm1?.date_of_birth);
    const joinDateParts = getDateParts(currentForm1?.join_date);
    const moveInDateParts = getDateParts(currentForm1?.move_in_date);

    const mergedFields = {
      loan_amount: numberWithCommas(currentForm1?.loan_amount),
      loan_purpose: currentForm1?.loan_purpose,
      first_name: currentForm1?.first_name,
      last_name: currentForm1?.last_name,
      gender: Number(`${currentForm1?.cust_gender ?? ''}`.replace('0', '')),
      hkid1: currentHkidParts?.[0],
      hkid2: currentHkidParts?.[1],
      hkid3: currentHkidParts?.[2],
      date_of_birth3: dateOfBirthParts.year,
      date_of_birth2: dateOfBirthParts.month,
      date_of_birth1: dateOfBirthParts.day,
      phone_number: currentForm1?.phone_number,
      area: currentForm1?.area,
      district: currentForm1?.district_value,
      addr_line1_room: currentForm1?.addr_line1_room,
      addr_line1_floor: currentForm1?.addr_line1_floor,
      addr_line1_block: currentForm1?.addr_line1_block,
      addr_line2: currentForm1?.addr_line2,
      ref_recommender_no: currentForm1?.ref_recommender_no,
      education: currentForm1?.education,
      marriage_status: currentForm1?.marriage_status,
      email: currentForm1?.email,
      messenger_id: currentForm1?.messenger_id,
      work_name: currentForm1?.work_name,
      work_area: currentForm1?.work_area,
      work_district: currentForm1?.work_district,
      work_addr: currentForm1?.work_addr,
      work_addr_line1_room: currentForm1?.work_addr_line1_room,
      work_addr_line1_floor: currentForm1?.work_addr_line1_floor,
      work_addr_line1_block: currentForm1?.work_addr_line1_block,
      work_addr_line2: currentForm1?.work_addr_line2,
      work_tel_no2: currentForm1?.work_tel_no2,
      join_date3: joinDateParts.year,
      join_date2: joinDateParts.month,
      join_date1: joinDateParts.day,
      pay_day: currentForm1?.pay_day,
      resi_own_sep: currentForm1?.resi_own_sep,
      monthly_rent: numberWithCommas(currentForm1?.monthly_rent),
      number_living_with: currentForm1?.number_living_with,
      move_in_date3: moveInDateParts.year,
      move_in_date2: moveInDateParts.month,
      move_in_date1: moveInDateParts.day,
      home_tel_no: currentForm1?.home_tel_no ?? currentForm1?.resi_tel_no2,
    };

    Object.entries(mergedFields).forEach(([fieldName, value]) => {
      if (value !== undefined && value !== null) {
        setValue(fieldName, value);
      }
    });

    setMergedDistrictOptions(getDistrictOptionsByArea(currentForm1?.area, currentForm1?.address_config));
    setMergedWorkDistrictOptions(getDistrictOptionsByArea(currentForm1?.work_area, currentForm1?.address_config));
  }, [currentForm1, isForm1MergedFlow, matchedDraft?.updatedAt, setValue]);

  const buildWorkAddressSnapshot = (data = {}) => {
    const workAreaValue = data?.work_area ?? getValues('work_area') ?? currentForm1?.work_area;
    const workDistrictValue = data?.work_district ?? getValues('work_district') ?? currentForm1?.work_district;
    const workArea = (currentForm1?.address_config?.area ?? options.area).find((item) => item.value === workAreaValue);
    const workDistrict = getDistrictOptionsByArea(workAreaValue, currentForm1?.address_config).find((item) => item.value === workDistrictValue);
    const room = data?.work_addr_line1_room ?? getValues('work_addr_line1_room') ?? currentForm1?.work_addr_line1_room ?? '';
    const floor = data?.work_addr_line1_floor ?? getValues('work_addr_line1_floor') ?? currentForm1?.work_addr_line1_floor ?? '';
    const block = data?.work_addr_line1_block ?? getValues('work_addr_line1_block') ?? currentForm1?.work_addr_line1_block ?? '';
    const addrLine2 = data?.work_addr_line2 ?? getValues('work_addr_line2') ?? currentForm1?.work_addr_line2 ?? '';
    const addrLine1 = buildAddressLine1(room, floor, block);
    const workAddr = [workArea?.label, workDistrict?.label, addrLine1, addrLine2].filter(Boolean).join(' ').trim();

    return {
      work_area: workAreaValue,
      work_district: workDistrictValue,
      work_province_id: workArea?.province_id ?? currentForm1?.work_province_id,
      work_city_id: workDistrict?.city_id ?? currentForm1?.work_city_id,
      work_city_id_tu: workDistrict?.tu_value ?? currentForm1?.work_city_id_tu,
      work_addr: workAddr || data?.work_addr || currentForm1?.work_addr,
      work_addr_line1_room: room,
      work_addr_line1_floor: floor,
      work_addr_line1_block: block,
      work_addr_line2: addrLine2,
    };
  };

  const buildForm1DraftSnapshot = (data = {}) => {
    const currentHkid1 = data?.hkid1 ?? getValues('hkid1') ?? currentForm1?.hkid_array?.[0] ?? currentForm1?.hkid?.[0] ?? '';
    const currentHkid2 = data?.hkid2 ?? getValues('hkid2') ?? currentForm1?.hkid_array?.[1] ?? currentForm1?.hkid?.slice?.(1, -1) ?? '';
    const currentHkid3 = data?.hkid3 ?? getValues('hkid3') ?? currentForm1?.hkid_array?.[2] ?? currentForm1?.hkid?.[currentForm1?.hkid?.length - 1] ?? '';
    const currentGenderValue = data?.gender ?? getValues('gender') ?? gender;

    const baseSnapshot = {
      ...currentForm1,
      loan_amount: data?.loan_amount ? numberWithNoCommas(data.loan_amount) : currentForm1?.loan_amount,
      loan_purpose: data?.loan_purpose ?? currentForm1?.loan_purpose,
      first_name: data?.first_name ?? currentForm1?.first_name,
      last_name: data?.last_name ?? currentForm1?.last_name,
      ref_recommender_no: data?.ref_recommender_no ?? currentForm1?.ref_recommender_no ?? '',
      cust_gender: currentGenderValue ? `0${currentGenderValue}` : currentForm1?.cust_gender,
      hkid: currentHkid1 && currentHkid2 && currentHkid3 ? `${currentHkid1}${currentHkid2}${currentHkid3}` : currentForm1?.hkid,
      hkid_array: [currentHkid1, currentHkid2, currentHkid3],
      date_of_birth: data?.date_of_birth3 ? `${data?.date_of_birth3}${pad(data?.date_of_birth2)}${pad(data?.date_of_birth1)}` : currentForm1?.date_of_birth,
      phone_number: currentForm1?.phone_number,
      email: data?.email ?? currentForm1?.email,
      education: data?.education ?? currentForm1?.education,
      marriage_status: data?.marriage_status ?? currentForm1?.marriage_status,
      messenger_id: data?.messenger_id ?? currentForm1?.messenger_id,
      application_form1_merged: currentForm1?.application_form1_merged,
    };

    if (!isForm1MergedFlow) {
      return baseSnapshot;
    }

    const selectedArea = data?.area ?? getValues('area') ?? currentForm1?.area;
    const selectedDistrict = data?.district ?? getValues('district') ?? currentForm1?.district_value;
    const district = getDistrictOptionsByArea(selectedArea, currentForm1?.address_config).find((item) => item.value === selectedDistrict);
    const province = currentForm1?.address_config?.area?.find((item) => item.value === selectedArea) ?? options.area.find((item) => item.value === selectedArea);
    const room = data?.addr_line1_room ?? getValues('addr_line1_room') ?? currentForm1?.addr_line1_room ?? '';
    const floor = data?.addr_line1_floor ?? getValues('addr_line1_floor') ?? currentForm1?.addr_line1_floor ?? '';
    const block = data?.addr_line1_block ?? getValues('addr_line1_block') ?? currentForm1?.addr_line1_block ?? '';
    const addrLine1 = buildAddressLine1(room, floor, block) || currentForm1?.addr_line1;
    const workAddressSnapshot = buildWorkAddressSnapshot(data);
    const joinDate = formatDateParts(data?.join_date3, data?.join_date2, data?.join_date1) || currentForm1?.join_date;
    const moveInDate = formatDateParts(data?.move_in_date3, data?.move_in_date2, data?.move_in_date1) || currentForm1?.move_in_date;

    return {
      ...baseSnapshot,
      area: selectedArea,
      city_id: district?.city_id ?? currentForm1?.city_id,
      city_id_tu: district?.tu_value ?? currentForm1?.city_id_tu,
      province_id: province?.province_id ?? currentForm1?.province_id,
      district_value: selectedDistrict,
      addr_line1: addrLine1,
      addr_line1_room: room,
      addr_line1_floor: floor,
      addr_line1_block: block,
      addr_line2: data?.addr_line2 ?? getValues('addr_line2') ?? currentForm1?.addr_line2,
      work_name: data?.work_name ?? currentForm1?.work_name,
      ...workAddressSnapshot,
      work_tel_no2: data?.work_tel_no2 ?? currentForm1?.work_tel_no2,
      join_date: joinDate,
      pay_day: data?.pay_day ?? currentForm1?.pay_day,
      resi_own_sep: data?.resi_own_sep ?? currentForm1?.resi_own_sep,
      monthly_rent: data?.monthly_rent !== undefined ? numberWithNoCommas(data.monthly_rent) : currentForm1?.monthly_rent,
      number_living_with: data?.number_living_with ?? currentForm1?.number_living_with,
      move_in_date: moveInDate,
      home_tel_no: data?.home_tel_no ?? data?.resi_tel_no2 ?? currentForm1?.home_tel_no ?? currentForm1?.resi_tel_no2,
      resi_tel_no2: data?.home_tel_no ?? data?.resi_tel_no2 ?? currentForm1?.home_tel_no ?? currentForm1?.resi_tel_no2,
    };
  };

  const buildForm21DraftSnapshot = (data = {}) => {
    return {
      ...currentForm2,
      work_name: data?.work_name ?? currentForm2?.work_name,
      work_addr: data?.work_addr ?? currentForm2?.work_addr,
      work_tel_no2: data?.work_tel_no2 ?? currentForm2?.work_tel_no2,
      join_date: data?.join_date ?? currentForm2?.join_date,
      pay_day: data?.pay_day ?? currentForm2?.pay_day,
      business_type: data?.business_type ?? currentForm2?.business_type,
      job_type: data?.job_type ?? currentForm2?.job_type,
      position: data?.position ?? currentForm2?.position,
      employment_type: data?.employment_type ?? currentForm2?.employment_type,
      receipt_way: data?.receipt_way ?? currentForm2?.receipt_way,
      monthly_income: data?.monthly_income ? numberWithNoCommas(data?.monthly_income) : currentForm2?.monthly_income,
      payroll_method: data?.payroll_method ?? currentForm2?.payroll_method,
      mpf: data?.mpf ?? currentForm2?.mpf,
      application_form1_merged: currentForm2?.application_form1_merged,
    };
  };

  const buildForm22DraftSnapshot = (data = {}) => {
    const selectedArea = data?.area ?? currentForm3?.area;
    const selectedDistrict = data?.district ?? getValues('district') ?? currentForm3?.district_value;
    const district = getDistrictOptionsByArea(selectedArea, currentForm3?.address_config).find((item) => item.value === selectedDistrict);
    const province = currentForm3?.address_config?.area?.find((item) => item.value === selectedArea) ?? options.area.find((item) => item.value === selectedArea);

    return {
      ...currentForm3,
      area: selectedArea,
      city_id: district?.city_id ?? currentForm3?.city_id,
      city_id_tu: district?.tu_value ?? currentForm3?.city_id_tu,
      province_id: province?.province_id ?? currentForm3?.province_id,
      district_value: selectedDistrict,
      addr_line1: data?.building ?? currentForm3?.addr_line1,
      addr_line2: data?.estate ?? currentForm3?.addr_line2,
      estate: data?.estate ?? currentForm3?.estate ?? currentForm3?.addr_line2,
      property_type: data?.property_type ?? currentForm3?.property_type,
      number_living_with: data?.number_living_with ?? currentForm3?.number_living_with,
      property_owner: data?.property_owner ?? currentForm3?.property_owner,
      resi_own_sep: data?.resi_own_sep ?? currentForm3?.resi_own_sep,
      move_in_date: data?.move_in_date ?? currentForm3?.move_in_date,
      home_tel_no: data?.home_tel_no ?? data?.resi_tel_no2 ?? currentForm3?.home_tel_no ?? currentForm3?.resi_tel_no2,
      monthly_rent: data?.monthly_rent ? numberWithNoCommas(data?.monthly_rent) : currentForm3?.monthly_rent,
      application_form1_merged: currentForm3?.application_form1_merged,
    };
  };

  const persistCurrentConfirmDraft = (currentStep = 'confirm') => {
    if (!flowMeta?.phone) {
      return null;
    }

    const currentValues = getValues();
    const form1 = buildForm1DraftSnapshot(currentValues);

    if (isForm1MergedFlow) {
      saveApplicationDraft({
        meta: flowMeta,
        currentStep,
        form1,
        form21: null,
        form22: null,
      });

      return { form1 };
    }

    const form21 = buildForm21DraftSnapshot(currentValues);
    const form22 = buildForm22DraftSnapshot(currentValues);

    saveApplicationDraft({
      meta: flowMeta,
      currentStep,
      form1,
      form21,
      form22,
    });

    return { form1, form21, form22 };
  };

  persistOnUnmountRef.current = () => persistCurrentConfirmDraft('confirm');

  const navigateToPreviousStep = ({ replace = false, useBrowserHistory = false } = {}) => {
    const snapshot = persistCurrentConfirmDraft(isForm1MergedFlow ? 'form1' : 'form22');

    if (isForm1MergedFlow) {
      const state = buildConsentRouteState(flowMeta ?? matchedDraft?.meta);

      if (!state) {
        return;
      }

      skipPersistOnUnmountRef.current = true;

      if (useBrowserHistory && canMoveApplicationFlowHistoryBack()) {
        navigate(-1);
        return;
      }

      navigate('/application-form-1', {
        replace,
        state,
      });
      return;
    }

    const previousRoute = buildApplicationFlowPreviousRoute({
      step: APPLICATION_FLOW_STEP.CONFIRM,
      draft: {
        form1: snapshot?.form1 ?? currentForm1,
        form21: snapshot?.form21 ?? currentForm2,
      },
      pageState,
      lendelaData,
    });

    if (!previousRoute) {
      return;
    }

    skipPersistOnUnmountRef.current = true;

    if (useBrowserHistory && canMoveApplicationFlowHistoryBack()) {
      navigate(-1);
      return;
    }

    navigate(previousRoute.pathname, {
      replace,
      state: previousRoute.state,
    });
  };

  const onClickPrevious = () => {
    navigateToPreviousStep({ useBrowserHistory: true });
  };

  const onSubmit = async (data) => {
    // check hkid valid
    if (!checkId(getValues('hkid1'), getValues('hkid2'), getValues('hkid3'))) {
      setErrorMessage('身份證格式錯誤，請重新輸入');
      window.scrollTo(0, 0);
      return;
    }
    if (data?.building?.length > 50) return;
    if (data?.estate?.length > 50) return;

    if (isForm1MergedFlow) {
      const form1 = buildForm1DraftSnapshot(data);
      const selectedArea = form1?.area;
      const selectedDistrict = form1?.district_value;
      const district = getDistrictOptionsByArea(selectedArea, form1?.address_config).find((item) => item.value === selectedDistrict);
      const province = (form1?.address_config?.area ?? options.area).find((item) => item.value === selectedArea);
      const applicationDate = moment().format('YYYY-MM-DDTHH:mm:ss') + 'Z';
      const utmMedium = sessionStorage.getItem('utm_medium');
      const utmSource = sessionStorage.getItem('utm_source');
      const utmCampaign = sessionStorage.getItem('utm_campaign');
      const utmId = sessionStorage.getItem('utm_id');
      const utmTerm = sessionStorage.getItem('utm_term');
      const utmContent = sessionStorage.getItem('utm_content');
      const sessionId = localStorage.getItem('session_id');
      const accessQuery = `utm_medium=${utmMedium ?? ''}&utm_source=${utmSource ?? ''}&utm_campaign=${utmCampaign ?? ''}&utm_id=${utmId ?? ''}&utm_term=${utmTerm ?? ''}&utm_content=${utmContent ?? ''}`;

      const applicationFormData = {
        first_name: form1?.first_name,
        last_name: form1?.last_name,
        phone_number: form1?.phone_number,
        hkid: form1?.hkid,
        date_of_birth: form1?.date_of_birth,
        cust_gender: form1?.cust_gender,
        ref_recommender_no: form1?.ref_recommender_no ?? '',
        loan_amount: parseInt(numberWithNoCommas(form1?.loan_amount), 10),
        agree_doc: parseAgreeDoc(form1?.agree_doc),
        area: selectedArea,
        province_id: form1?.province_id ?? province?.province_id,
        city_id: form1?.city_id ?? district?.city_id,
        city_id_tu: form1?.city_id_tu ?? district?.tu_value,
        addr_line1: form1?.addr_line1,
        addr_line1_room: form1?.addr_line1_room,
        addr_line1_floor: form1?.addr_line1_floor ?? '',
        addr_line1_block: form1?.addr_line1_block ?? '',
        addr_line2: form1?.addr_line2,
      };

      appendOptionalPayload(applicationFormData, {
        loan_purpose: form1?.loan_purpose,
        education: form1?.education,
        marriage_status: form1?.marriage_status,
        email: form1?.email,
        messenger_id: form1?.messenger_id,
        work_name: form1?.work_name,
        work_addr: form1?.work_addr,
        work_area: form1?.work_area,
        work_district: form1?.work_district,
        work_province_id: form1?.work_province_id,
        work_city_id: form1?.work_city_id,
        work_city_id_tu: form1?.work_city_id_tu,
        work_addr_line1_room: form1?.work_addr_line1_room,
        work_addr_line1_floor: form1?.work_addr_line1_floor,
        work_addr_line1_block: form1?.work_addr_line1_block,
        work_addr_line2: form1?.work_addr_line2,
        work_tel_no2: form1?.work_tel_no2,
        join_date: form1?.join_date,
        pay_day: form1?.pay_day,
        resi_own_sep: form1?.resi_own_sep,
        monthly_rent: toOptionalInteger(form1?.monthly_rent),
        number_living_with: form1?.number_living_with,
        move_in_date: form1?.move_in_date,
        resi_tel_no2: form1?.home_tel_no ?? form1?.resi_tel_no2,
      });

      const dataForm = {
        application_date: applicationDate,
        application_ip: form1?.application_ip,
        application_browser: window.navigator.userAgent,
        application_device: detectMob() ? 2 : 1,
        first_vist_date: applicationDate,
        apply_access_data: {
          access_data_id: '',
          access_referer: 'No Referer',
          access_query: accessQuery,
          utm_source: utmSource ?? '',
          utm_medium: utmMedium ?? '',
          session_id: sessionId ?? '',
        },
        lendela_id: lendelaData?.id ?? null,
        application_form_data: applicationFormData,
      };

      saveApplicationDraft({
        meta: flowMeta,
        currentStep: 'confirm',
        form1,
        form21: null,
        form22: null,
      });

      const completeRes = await dispatch(actionApplyOnly(dataForm));

      if (completeRes?.sts_code === 0) {
        clearApplicationDraft();
        skipPersistOnUnmountRef.current = true;
        navigate('/application/confirm/complete', {
          replace: true,
          state: {
            status: true,
            content: {
              request_id: completeRes?.content?.request_id,
            },
            lendela_id: lendelaData?.id,
          },
        });
        return;
      }

      if (completeRes?.sts_code === 3) {
        clearApplicationDraft();
        localStorage.removeItem('access_token');
        localStorage.removeItem('application_phone');
        setIsVisibleInvalidToken(true);
        return;
      }

      setErrorMessage('提交申請失敗，請稍後再試');
      window.scrollTo(0, 0);
      return;
    }

    const form1Payload = {
      loan_amount: numberWithNoCommas(data?.loan_amount ?? currentForm1?.loan_amount),
      loan_purpose: data?.loan_purpose ?? currentForm1?.loan_purpose,
      first_name: data?.first_name ?? currentForm1?.first_name,
      last_name: data?.last_name ?? currentForm1?.last_name,
      ref_recommender_no: data?.ref_recommender_no ?? currentForm1?.ref_recommender_no ?? '',
      cust_gender: `0${gender}`,
      hkid: data?.hkid1 ? `${data?.hkid1}${data?.hkid2}${data?.hkid3}` : currentForm1?.hkid,
      date_of_birth: data?.date_of_birth3 ? `${data?.date_of_birth3}${pad(data?.date_of_birth2)}${pad(data?.date_of_birth1)}` : currentForm1?.date_of_birth,
      phone_number: currentForm1?.phone_number,
      email: data?.email ?? currentForm1?.email,
      education: data?.education ?? currentForm1?.education,
      marriage_status: data?.marriage_status ?? currentForm1?.marriage_status,
      application_device: detectMob() ? 2 : 1,
      application_ip: currentForm1?.application_ip,
      application_browser: window.navigator.userAgent,
      check_box_1: currentForm1?.check_box_1,
      check_box_2: currentForm1?.check_box_2,
      check_box_1_time: currentForm1?.check_box_1_time,
      check_box_2_time: currentForm1?.check_box_2_time,
      agree_doc: currentForm1?.agree_doc,
      exclude_upload_s3: true,
    };

    const form21Payload = {
      business_type: data?.business_type ?? currentForm2?.business_type,
      job_type: data?.job_type ?? currentForm2?.job_type,
      position: data?.position ?? currentForm2?.position,
      employment_type: data?.employment_type ?? currentForm2?.employment_type,
      receipt_way: data?.receipt_way ?? currentForm2?.receipt_way,
      monthly_income: numberWithNoCommas(data?.monthly_income ?? currentForm2?.monthly_income),
      payroll_method: data?.payroll_method ?? currentForm2?.payroll_method,
      mpf: data?.mpf ?? currentForm2?.mpf,
      exclude_upload_s3: true,
    };

    const selectedArea = data?.area ?? currentForm3?.area;
    const selectedDistrict = data?.district ?? currentForm3?.district_value;
    const district = getDistrictOptionsByArea(selectedArea).find((item) => item.value === selectedDistrict);
    const province = options.area.find(t => t.value === selectedArea);

    const form22Payload = {
      area: selectedArea,
      city_id: district?.city_id,
      city_id_tu: district?.tu_value,
      province_id: province?.province_id,
      addr_line1: data?.building ?? currentForm3?.addr_line1,
      addr_line2: data?.estate ?? currentForm3?.addr_line2,
      property_type: data?.property_type ?? currentForm3?.property_type,
      number_living_with: data?.number_living_with ?? currentForm3?.number_living_with,
      property_owner: data?.property_owner ?? currentForm3?.property_owner,
      monthly_rent: numberWithNoCommas(data?.monthly_rent ?? currentForm3?.monthly_rent),
      exclude_upload_s3: true,
    };
    await dispatch(
      actionSubmitForm1(form1Payload),
    );
    await dispatch(
      actionSubmitForm21(form21Payload),
    );
    await dispatch(
      actionSubmitForm22(form22Payload),
    );
    const response = await dispatch(actionConfirmData());

    if (response?.sts_code === 0) {
      // ✅ AddressForm에서 쓰는 것과 동일한 형태로 dataForm 구성
      const application_date = moment().format('YYYY-MM-DDTHH:mm:ss') + 'Z';

      const utm_medium = sessionStorage.getItem('utm_medium');
      const utm_source = sessionStorage.getItem('utm_source');
      const utm_campaign = sessionStorage.getItem('utm_campaign');
      const utm_id = sessionStorage.getItem('utm_id');
      const utm_term = sessionStorage.getItem('utm_term');
      const utm_content = sessionStorage.getItem('utm_content');
      const session_id = localStorage.getItem('session_id');
      const access_query = `utm_medium=${utm_medium ?? ''}&utm_source=${utm_source ?? ''}&utm_campaign=${utm_campaign ?? ''}&utm_id=${utm_id ?? ''}&utm_term=${utm_term ?? ''}&utm_content=${utm_content ?? ''}`;

      // form1은 지금 화면에서 이미 location.state?.form1에 존재
      const form1 = {
        ...currentForm1,
        ...form1Payload,
      };

      const form2 = {
        ...currentForm2,
        ...form21Payload,
      };

      const dataForm = {
        application_date,
        application_ip: form1?.application_ip,
        application_browser: window.navigator.userAgent,
        application_device: detectMob() ? 2 : 1,
        first_vist_date: application_date,

        apply_access_data: {
          access_data_id: '',
          access_referer: 'No Referer',
          access_query,
          utm_source: utm_source ?? '',
          utm_medium: utm_medium ?? '',
          session_id: session_id ?? '',
        },

        lendela_id: lendelaData?.id ?? null,

        application_form_data: {
          /* ======================
          * form1 (개인정보)
          * ====================== */
          first_name: form1?.first_name,
          last_name: form1?.last_name,
          phone_number: form1?.phone_number,
          email: form1?.email,
          hkid: form1?.hkid,
          date_of_birth: form1?.date_of_birth,
          cust_gender: form1?.cust_gender,
          ref_recommender_no: form1?.ref_recommender_no ?? '',
          education: form1?.education,
          marriage_status: form1?.marriage_status,
          loan_amount: parseInt(numberWithNoCommas(form1?.loan_amount), 10),
          loan_purpose: form1?.loan_purpose,
          agree_doc:
            typeof form1?.agree_doc === 'string'
              ? JSON.parse(form1?.agree_doc)
              : form1?.agree_doc,

          /* ======================
          * form21 (직업정보)
          * ====================== */
          business_type: form2?.business_type,
          job_type: form2?.job_type,
          position: form2?.position,
          employment_type: form2?.employment_type,
          receipt_way: form2?.receipt_way,
          monthly_income: parseInt(
            numberWithNoCommas(form2?.monthly_income),
            10,
          ),
          payroll_method: form2?.payroll_method,
          mpf: form2?.mpf,

          /* ======================
          * ✅ form22 (주소정보) – 요청하신 부분
          * ====================== */
          area: form22Payload?.area,
          province_id: province?.province_id,
          city_id: district?.city_id,
          city_id_tu: district?.tu_value,

          addr_line1: form22Payload?.addr_line1,
          addr_line2: form22Payload?.addr_line2,

          property_type: form22Payload?.property_type,
          number_living_with: form22Payload?.number_living_with,
          property_owner: form22Payload?.property_owner,

          monthly_rent: parseInt(
            numberWithNoCommas(form22Payload?.monthly_rent),
            10,
          ),
        },
      };

      // ✅ 여기서 “완료 신청” API 호출
      const completeRes = await dispatch(actionApplyOnly(dataForm));

      // completeWithoutEkyc 실패 시 처리
      if (completeRes?.sts_code !== 0) {
        // 필요하면 에러 팝업 처리
        setErrorMessage('提交申請失敗，請稍後再試');
        return;
      }

      const requestId = completeRes?.content?.request_id;//현재는 사용안함... 향후 필요하면 목적지 페이지에서 사용하면됨.

      // ✅ 성공하면 완료 페이지로 이동
      skipPersistOnUnmountRef.current = true;
      navigate('/application/confirm/complete', {
        replace: true,
        state: {
          status: true,
          content: {
            request_id: requestId,
          },
          lendela_id: lendelaData?.id,
        },
      });
    } else if (response?.sts_code === 3) {
      // INVALID_TOKEN
      clearApplicationDraft();
      localStorage.removeItem('access_token');
      localStorage.removeItem('application_phone');
      setIsVisibleInvalidToken(true);
    }
  };

  useEffect(() => {
    const beforeunload = (e) => {
      persistCurrentConfirmDraft('confirm');
      const event = e || window.event;
      if (event) {
        event.returnValue = 'Sure?';
      }
      return 'Sure?';
    };

    let currentHistoryIndex = getApplicationFlowHistoryIndex();

    const handlePopState = (event) => {
      const shouldMoveToPreviousStep = isApplicationFlowBackPopState(event, currentHistoryIndex);
      currentHistoryIndex = getApplicationFlowHistoryIndex();

      if (shouldMoveToPreviousStep) {
        navigateToPreviousStep({ replace: true });
        return;
      }

      persistCurrentConfirmDraft('confirm');
    };

    window.addEventListener('beforeunload', beforeunload);
    window.addEventListener('popstate', handlePopState, true);

    return () => {
      window.removeEventListener('beforeunload', beforeunload);
      window.removeEventListener('popstate', handlePopState, true);
    };
  }, [flowMeta?.lendelaAppId, flowMeta?.phone, gender, navigateToPreviousStep]);

  useEffect(() => {
    return () => {
      if (!skipPersistOnUnmountRef.current && persistOnUnmountRef.current) {
        persistOnUnmountRef.current();
      }
    };
  }, []);

  const checkId = (hkid1, hkid2, hkid3) => {
    if (hkid1 && hkid2 && hkid3) {
      return validid.hkid(`${hkid1}${hkid2}/${hkid3}`);
    }
    return true;
  };

  const refRecommenderNoField = register('ref_recommender_no', {
    validate: isValidRefRecommenderNo,
  });

  const confirmForm1 = location.state?.form1 ?? currentForm1;
  const getOptionLabel = (list, value) => list?.find((item) => item?.value === value)?.label ?? '';
  const getDistrictLabel = (area, districtValue, addressConfig) => getDistrictOptionsByArea(area, addressConfig).find((item) => item.value === districtValue)?.label ?? '';
  const readOnlyValueClass = 'text-primary flex-1 break-words text-right';
  const readOnlyAlignProps = { readOnlyRightAlign: true };
  const renderValueRow = (title, value) => (
    <div className="flex items-start mt-2">
      <p className="text-gray w-1/2">{title}</p>
      <p className={readOnlyValueClass}>{hasValue(value) ? value : ''}</p>
    </div>
  );
  const renderMergedTextInput = ({ title, field, value, required = false, maxLength, inputMode, placeholder, disabled, validate, pattern, patternErrorMessage, validateErrorMessage, onChange }) => (
    <div className="mt-4">
      <div className="flex items-center">
        <p className="text-gray w-1/2">{title}</p>
        <input
          disabled={disabled}
          defaultValue={value ?? ''}
          maxLength={maxLength}
          inputMode={inputMode}
          placeholder={placeholder}
          {...register(field, {
            required,
            validate,
            pattern,
            onChange: (e) => {
              if (onChange) {
                onChange(e);
              }
            },
          })}
          className="w-1/2 pr-2 outline-none text-primary border-primary border-b"
          style={{ height: 36 }}
        />
      </div>
      {errors?.[field]?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
      {errors?.[field]?.type === 'pattern' && <span className="text-10 text-red-500">{patternErrorMessage ?? '*請輸入正確的資料'}</span>}
      {errors?.[field]?.type === 'validate' && <span className="text-10 text-red-500">{validateErrorMessage ?? '*請輸入正確的資料'}</span>}
    </div>
  );
  const renderMergedMoneyInput = ({ title, field, value, required = false, placeholder, zeroAllow = false, max = null, maxDigits }) => (
    <div className="mt-4">
        <div className="flex items-center">
          <p className="text-gray w-1/2">{title}</p>
        <span className="text-primary w-1/2 flex items-center border-primary border-b">
          HK$&nbsp;
          <input
            inputMode="numeric"
            defaultValue={numberWithCommas(value)}
            placeholder={placeholder}
            {...register(field, {
              required,
              pattern: /^[0-9,]*$/,
              onChange: (e) => {
                const limitedValue = maxDigits ? numberWithNoCommas(e.target.value).slice(0, maxDigits) : e.target.value;

                if ((limitedValue || '').startsWith('0') && !zeroAllow) {
                  e.target.value = '';
                  setValue(field, '');
                  return;
                }

                if (max && limitedValue && Number(numberWithNoCommas(limitedValue)) > max) {
                  e.target.value = numberWithCommas(max);
                  setValue(field, e.target.value);
                  return;
                }

                e.target.value = numberWithCommas(limitedValue);
                setValue(field, e.target.value);
                setError(field, null);
              },
            })}
            className="w-full outline-none"
            style={{ height: 36 }}
          />
        </span>
      </div>
      {errors?.[field]?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
      {errors?.[field]?.type === 'pattern' && <span className="text-10 text-red-500">*无效的</span>}
    </div>
  );
  const renderMergedSelect = ({ title, field, value, selectOptions, required = false, disabled, onChange }) => (
    <div className="flex items-center mt-4">
      <p className="text-gray w-1/2">{title}</p>
      <div className="relative w-1/2 border-primary border-b px-3">
        <Select
          placeholder="- 請選擇 -"
          className="w-full"
          theme={(theme) => ({
            ...theme,
            borderRadius: 0,
          })}
          styles={{
            ...styles,
            valueContainer: (base) => ({
              ...base,
              paddingLeft: 0,
            }),
          }}
          defaultValue={value ? selectOptions?.find((option) => option?.value === value) : null}
          {...register(field, { required })}
          onChange={(selectedOption) => {
            setValue(field, selectedOption?.value);
            setError(field, null);
            if (onChange) {
              onChange(selectedOption);
            }
          }}
          options={selectOptions}
          isSearchable={false}
          isDisabled={disabled}
        />
      </div>
      {errors?.[field]?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
    </div>
  );
  const renderMergedDateInput = ({ title, selectedDate, fields, required = false, ageLimit = false, limitToToday = false, restrictBoundaryOptions = true, disabled }) => (
    (() => {
      const normalizedDate = selectedDate ? `${selectedDate}`.replace(/[-/]/g, '') : '';
      const yearField = fields?.year ?? 'date_of_birth3';
      const monthField = fields?.month ?? 'date_of_birth2';
      const dayField = fields?.day ?? 'date_of_birth1';
      const selectedYear = normalizedDate.length === 8 ? normalizedDate.substring(0, 4) : '';
      const selectedMonth = normalizedDate.length === 8 ? String(parseInt(normalizedDate.substring(4, 6), 10)) : '';
      const selectedDay = normalizedDate.length === 8 ? String(parseInt(normalizedDate.substring(6, 8), 10)) : '';
      const currentYearValue = resolveDateFieldValue(watch(yearField), selectedYear);
      const currentMonthValue = resolveDateFieldValue(watch(monthField), selectedMonth);
      const currentDayValue = resolveDateFieldValue(watch(dayField), selectedDay);
      const yearOptions = ageLimit ? renderRange(moment().subtract(60, 'years').format('YYYY'), moment().subtract(18, 'years').format('YYYY'), true) : DATE_YEAR_OPTIONS;
      const monthOptions = getDateMonthOptions({ selectedYear: currentYearValue, ageLimit, limitToToday, restrictBoundaryOptions });
      const dayOptions = getDateDayOptions({ selectedYear: currentYearValue, selectedMonth: currentMonthValue, ageLimit, limitToToday, restrictBoundaryOptions });
      const requiredRule = required ? { required: true } : {};
      const compactSelectStyles = {
        ...styles,
        control: (base) => ({
          ...base,
          border: 0,
          borderRadius: 0,
          boxShadow: 'none',
          backgroundColor: 'transparent',
          minHeight: 36,
          '&:hover': {
            border: 0,
          },
        }),
        indicatorSeparator: () => ({
          display: 'none',
        }),
        valueContainer: (base) => ({
          ...base,
          padding: 0,
        }),
        singleValue: (base) => ({
          ...base,
          color: '#FD9F40',
          overflow: 'visible',
          textOverflow: 'clip',
        }),
        dropdownIndicator: (base) => ({
          ...base,
          color: '#FD9F40',
          padding: 0,
        }),
      };

      return (
        <div className="mt-4">
          <div className="flex items-center">
            <p className="text-gray w-1/2">{title}</p>
            <div className="w-1/2 flex items-center gap-2">
              <div className="w-[46%] border-b border-primary">
                <Select
                  placeholder="年"
                  className="w-full text-center"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={compactSelectStyles}
                  value={yearOptions.find((option) => option.value === currentYearValue) ?? null}
                  {...register(yearField, requiredRule)}
                  onChange={(selectedOption) => {
                    setValue(yearField, selectedOption?.value);
                    setError(yearField, null);
                    if (limitToToday || (ageLimit && restrictBoundaryOptions)) {
                      setValue(monthField, null);
                      setValue(dayField, null);
                      setError(monthField, null);
                      setError(dayField, null);
                    }
                  }}
                  options={yearOptions}
                  isSearchable={false}
                  isDisabled={disabled}
                />
              </div>
              <div className="w-[27%] border-b border-primary">
                <Select
                  placeholder="月"
                  className="w-full text-center"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={compactSelectStyles}
                  value={monthOptions.find((option) => option.value === currentMonthValue) ?? null}
                  {...register(monthField, requiredRule)}
                  onChange={(selectedOption) => {
                    setValue(monthField, selectedOption?.value);
                    setError(monthField, null);
                    if (limitToToday || (ageLimit && restrictBoundaryOptions)) {
                      setValue(dayField, null);
                      setError(dayField, null);
                    }
                  }}
                  options={monthOptions}
                  isSearchable={false}
                  isDisabled={disabled}
                />
              </div>
              <div className="w-[27%] border-b border-primary">
                <Select
                  placeholder="日"
                  className="w-full text-center"
                  theme={(theme) => ({
                    ...theme,
                    borderRadius: 0,
                  })}
                  styles={compactSelectStyles}
                  value={dayOptions.find((option) => option.value === currentDayValue) ?? null}
                  {...register(dayField, {
                    ...requiredRule,
                    validate: (value) => {
                      const year = getValues(yearField);
                      const month = getValues(monthField);
                      if (value && month && year) {
                        return moment(`${year}/${month}/${value}`, 'YYYY/MM/DD').isValid();
                      }
                      return true;
                    },
                  })}
                  onChange={(selectedOption) => {
                    setValue(dayField, selectedOption?.value);
                    setError(dayField, null);
                  }}
                  options={dayOptions}
                  isSearchable={false}
                  isDisabled={disabled}
                />
              </div>
            </div>
          </div>
          {(errors?.[yearField]?.type === 'required' || errors?.[monthField]?.type === 'required' || errors?.[dayField]?.type === 'required') && <span className="text-10 text-red-500">*必填</span>}
          {errors?.[dayField]?.type === 'validate' && <span className="text-10 text-red-500">*无效的</span>}
        </div>
      );
    })()
  );
  const mergedAddressConfig = confirmForm1?.address_config;
  const mergedWorkName = confirmForm1?.work_name;
  const mergedWorkArea = confirmForm1?.work_area;
  const mergedWorkDistrict = confirmForm1?.work_district;
  const mergedWorkAddressLine1 = buildAddressLine1(confirmForm1?.work_addr_line1_room, confirmForm1?.work_addr_line1_floor, confirmForm1?.work_addr_line1_block);
  const mergedWorkAddr = confirmForm1?.work_addr
    || [getOptionLabel(mergedAddressConfig?.area ?? options.area, mergedWorkArea), getDistrictLabel(mergedWorkArea, mergedWorkDistrict, mergedAddressConfig), mergedWorkAddressLine1, confirmForm1?.work_addr_line2]
      .filter(Boolean)
      .join(' ')
      .trim();
  const hasMergedWorkAddressParts = [mergedWorkArea, mergedWorkDistrict, confirmForm1?.work_addr_line1_room, confirmForm1?.work_addr_line1_floor, confirmForm1?.work_addr_line1_block, confirmForm1?.work_addr_line2].some(hasValue);
  const mergedWorkTelNo = confirmForm1?.work_tel_no2;
  const mergedJoinDate = confirmForm1?.join_date;
  const mergedPayDay = confirmForm1?.pay_day;
  const mergedResiOwnSep = confirmForm1?.resi_own_sep;
  const mergedMoveInDate = confirmForm1?.move_in_date;
  const mergedHomeTelNo = confirmForm1?.home_tel_no ?? confirmForm1?.resi_tel_no2;
  const mergedArea = confirmForm1?.area;
  const mergedDistrict = confirmForm1?.district_value;
  const mergedHkidParts = getHkidParts(confirmForm1);
  const hasMergedHkidRequiredError = errors?.hkid1?.type === 'required'
    || errors?.hkid2?.type === 'required'
    || errors?.hkid3?.type === 'required';

  if (!location.state?.form1 && restoredState?.form1) {
    return null;
  }

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      {isVisibleInvalidToken && <PopUpBox.InvalidToken />}
      <Common.Loading isVisible={isSubmitting} />
      {errorMessage && <PopUpBox.Error content={errorMessage} onClick={() => setErrorMessage(null)}>
        <div className="text-center my-3">
          <button onClick={() => setErrorMessage(null)} type="button" className="rounded-full text-white text-sm bg-lightningYellow px-14 py-2">
            確定
          </button>
        </div>
      </PopUpBox.Error>}
      <div className="bg-white p-5">
        <Common.StepBar active={2} />
        <p className="flex-1 text-2xl text-primary2 text-right font-semibold border-b border-gray2 mb-4 pb-4">請確認申請資料</p>
        <p className="text-gray">
          閣下可以點選<img src={Edit} alt="" className="inline" />修改相關部份的資料。
          請確認所填之資料，點選「確認資料無誤」後，
          便不能再作修改。
        </p>
        {isForm1MergedFlow ? (
          <div>
            <div className="border-primary border-b text-right flex justify-end items-center text-primary text-xl py-1 my-4">
              <p className={`mr-2 ${isEditingMergedForm ? '' : 'cursor-pointer'}`} onClick={() => !isEditingMergedForm && setIsEditingMergedForm(true)}>修改資料</p>
              {!isEditingMergedForm && <img style={{ width: 23, height: 23 }} src={Edit} alt="" onClick={() => setIsEditingMergedForm(true)} className="cursor-pointer" />}
            </div>
            {isEditingMergedForm ? (
              <>
                {renderMergedMoneyInput({
                  title: '貸款金額',
                  field: 'loan_amount',
                  value: confirmForm1?.loan_amount,
                  required: true,
                  placeholder: ' ',
                  max: 400000,
                })}
                {renderMergedTextInput({
                  title: '姓氏',
                  field: 'first_name',
                  value: confirmForm1?.first_name,
                  required: true,
                  disabled: !!lendelaData?.firstName,
                  onChange: (e) => {
                    e.target.value = alphabetOnly(e?.target?.value + '').toUpperCase();
                    setValue('first_name', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '名字',
                  field: 'last_name',
                  value: confirmForm1?.last_name,
                  required: true,
                  disabled: !!lendelaData?.firstName,
                  onChange: (e) => {
                    e.target.value = capitalizeName(alphabetOnly(e?.target?.value + ''));
                    setValue('last_name', e.target.value);
                  },
                })}
                {renderMergedSelect({
                  title: '性別',
                  field: 'gender',
                  value: Number(`${confirmForm1?.cust_gender ?? ''}`.replace('0', '')),
                  selectOptions: options.genders,
                  required: true,
                  onChange: (selectedOption) => setGender(selectedOption?.value),
                })}
                <div className="mt-4">
                  <p className="text-xl text-gray mb-2">香港身份證號碼</p>
                  <div className="flex items-center">
                      <input
                        disabled={!!lendelaData?.personalNumber}
                        maxLength={2}
                        defaultValue={mergedHkidParts?.[0] ?? ''}
                        {...register('hkid1', {
                          required: true,
                          onChange: (e) => {
                            e.target.value = alphabetOnly(e.target.value.toUpperCase().replace(' ', ''));
                            setValue('hkid1', e.target.value, { shouldValidate: true });
                          },
                        })}
                        className="px-2 outline-none text-primary w-1/3 text-center border-b border-primary"
                        placeholder="A"
                      />
                      <input
                        disabled={!!lendelaData?.personalNumber}
                        maxLength={6}
                        defaultValue={mergedHkidParts?.[1] ?? ''}
                        {...register('hkid2', {
                          required: true,
                          onChange: (e) => {
                            e.target.value = (e?.target?.value || '').replace(/[^0-9]/g, '');
                            setValue('hkid2', e.target.value, { shouldValidate: true });
                          },
                        })}
                        className="px-2 outline-none text-primary text-center ml-2 mr-2 w-full border-b border-primary"
                        placeholder="123456"
                      />
                      <div className="border-b border-primary">
                        <span className="text-primary">(</span>
                        <input
                          disabled={!!lendelaData?.personalNumber}
                          maxLength={1}
                          defaultValue={mergedHkidParts?.[2] ?? ''}
                          {...register('hkid3', {
                            required: true,
                            onChange: (e) => {
                              e.target.value = alphabetAndNumberOnly(e.target.value.toUpperCase().replace(' ', ''));
                              setValue('hkid3', e.target.value, { shouldValidate: true });
                            },
                          })}
                          className="px-2 outline-none text-primary w-3/4 text-center"
                          placeholder="3"
                        />
                        <span className="text-primary">)</span>
                      </div>
                  </div>
                  {hasMergedHkidRequiredError && <span className="text-10 text-red-500">*必填</span>}
                  {!hasMergedHkidRequiredError && !checkId(getValues('hkid1'), getValues('hkid2'), getValues('hkid3')) && (
                    <span className="text-10 text-red-500">*請輸入正確的身份證號碼</span>
                  )}
                </div>
                {renderMergedDateInput({
                  title: '出生日期',
                  selectedDate: confirmForm1?.date_of_birth,
                  required: true,
                  ageLimit: true,
                  disabled: !!lendelaData?.dateOfBirth,
                })}
                {renderValueRow('手提電話', confirmForm1?.phone_number)}
                {renderMergedSelect({
                  title: '區域',
                  field: 'area',
                  value: mergedArea,
                  selectOptions: mergedAddressConfig?.area ?? options.area,
                  required: true,
                  onChange: (selectedOption) => {
                    const nextOptions = getDistrictOptionsByArea(selectedOption?.value, mergedAddressConfig);
                    setMergedDistrictOptions(nextOptions);
                    setValue('district', null);
                  },
                })}
                {renderMergedSelect({
                  title: '地區',
                  field: 'district',
                  value: mergedDistrict,
                  selectOptions: mergedDistrictOptions.length ? mergedDistrictOptions : getDistrictOptionsByArea(mergedArea, mergedAddressConfig),
                  required: true,
                })}
                {renderMergedTextInput({
                  title: '單位',
                  field: 'addr_line1_room',
                  value: confirmForm1?.addr_line1_room,
                  required: true,
                  placeholder: 'Room/Flat/Unit',
                  onChange: (e) => {
                    e.target.value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setValue('addr_line1_room', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '樓層',
                  field: 'addr_line1_floor',
                  value: confirmForm1?.addr_line1_floor,
                  placeholder: 'Floor',
                  onChange: (e) => {
                    e.target.value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setValue('addr_line1_floor', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '座數/大廈',
                  field: 'addr_line1_block',
                  value: confirmForm1?.addr_line1_block,
                  placeholder: 'Block',
                  onChange: (e) => {
                    e.target.value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setValue('addr_line1_block', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '屋苑/街道',
                  field: 'addr_line2',
                  value: confirmForm1?.addr_line2,
                  required: true,
                  placeholder: '(E.g. HAPPY EST)',
                  onChange: (e) => {
                    e.target.value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setValue('addr_line2', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '推薦碼 (如有)',
                  field: 'ref_recommender_no',
                  value: confirmForm1?.ref_recommender_no,
                  maxLength: 6,
                  validate: isValidRefRecommenderNo,
                  validateErrorMessage: '推薦碼必須為6個字元',
                  onChange: (e) => {
                    e.target.value = normalizeRefRecommenderNo(e.target.value);
                    setValue('ref_recommender_no', e.target.value, { shouldValidate: true });
                  },
                })}
              </>
            ) : (
              <>
                {renderValueRow('貸款金額', hasValue(confirmForm1?.loan_amount) ? `HK$ ${confirmForm1?.loan_amount}` : '')}
                {renderValueRow('英文姓名', `${confirmForm1?.first_name ?? ''} ${confirmForm1?.last_name ?? ''}`.trim())}
                {renderValueRow('性別', confirmForm1?.cust_gender === '01' ? '先生' : confirmForm1?.cust_gender === '02' ? '小姐' : '')}
                {renderValueRow('香港身份證號碼', formatHkid(confirmForm1?.hkid))}
                {renderValueRow('出生日期', formatDisplayDate(confirmForm1?.date_of_birth))}
                {renderValueRow('手提電話', confirmForm1?.phone_number)}
                {renderValueRow('區域', getOptionLabel(mergedAddressConfig?.area ?? options.area, mergedArea))}
                {renderValueRow('地區', getDistrictLabel(mergedArea, mergedDistrict, mergedAddressConfig))}
                {renderValueRow('單位', confirmForm1?.addr_line1_room)}
                {renderValueRow('樓層', confirmForm1?.addr_line1_floor)}
                {renderValueRow('座數/大廈', confirmForm1?.addr_line1_block)}
                {renderValueRow('屋苑/街道', confirmForm1?.addr_line2)}
                {renderValueRow('推薦碼 (如有)', confirmForm1?.ref_recommender_no)}
              </>
            )}
            {isEditingMergedForm ? (
              <>
                {renderMergedSelect({
                  title: '貸款目的',
                  field: 'loan_purpose',
                  value: confirmForm1?.loan_purpose,
                  selectOptions: options.loan_purpose,
                  disabled: !!lendelaData?.loanPurpose,
                })}
                {renderMergedSelect({
                  title: '學歷',
                  field: 'education',
                  value: confirmForm1?.education,
                  selectOptions: options.education,
                })}
                {renderMergedSelect({
                  title: '婚姻狀態',
                  field: 'marriage_status',
                  value: confirmForm1?.marriage_status,
                  selectOptions: options.marriage_status,
                })}
                {renderMergedTextInput({
                  title: '電郵地址',
                  field: 'email',
                  value: confirmForm1?.email,
                  disabled: !!lendelaData?.email,
                  pattern: OPTIONAL_EMAIL_PATTERN,
                  patternErrorMessage: '*請輸入正確的電郵',
                  onChange: (e) => {
                    e.target.value = emailAddressOnly(e.target?.value);
                    setValue('email', e.target.value, { shouldValidate: true });
                  },
                })}
                {renderMergedTextInput({
                  title: 'Facebook/Instagram',
                  field: 'messenger_id',
                  value: confirmForm1?.messenger_id,
                  maxLength: 50,
                  validate: (value) => isOptionalMaxLength(value, 50),
                  onChange: (e) => {
                    e.target.value = (e?.target?.value || '').slice(0, 50);
                    setValue('messenger_id', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '公司名稱',
                  field: 'work_name',
                  value: mergedWorkName,
                  maxLength: 20,
                  validate: (value) => !value || companyNameOnly(value) === value,
                  onChange: (e) => {
                    e.target.value = companyNameOnly(e?.target?.value || '').toUpperCase().slice(0, 20);
                    setValue('work_name', e.target.value);
                  },
                })}
                {renderMergedSelect({
                  title: '區域',
                  field: 'work_area',
                  value: mergedWorkArea,
                  selectOptions: mergedAddressConfig?.area ?? options.area,
                  onChange: (selectedOption) => {
                    const nextOptions = getDistrictOptionsByArea(selectedOption?.value, mergedAddressConfig);
                    setMergedWorkDistrictOptions(nextOptions);
                    setValue('work_district', null);
                  },
                })}
                {renderMergedSelect({
                  title: '地區',
                  field: 'work_district',
                  value: mergedWorkDistrict,
                  selectOptions: mergedWorkDistrictOptions.length ? mergedWorkDistrictOptions : getDistrictOptionsByArea(mergedWorkArea, mergedAddressConfig),
                })}
                {renderMergedTextInput({
                  title: '單位',
                  field: 'work_addr_line1_room',
                  value: confirmForm1?.work_addr_line1_room,
                  placeholder: 'Room/Flat/Unit',
                  onChange: (e) => {
                    e.target.value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setValue('work_addr_line1_room', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '樓層',
                  field: 'work_addr_line1_floor',
                  value: confirmForm1?.work_addr_line1_floor,
                  placeholder: 'Floor',
                  onChange: (e) => {
                    e.target.value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setValue('work_addr_line1_floor', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '座數/大廈',
                  field: 'work_addr_line1_block',
                  value: confirmForm1?.work_addr_line1_block,
                  placeholder: 'Block',
                  onChange: (e) => {
                    e.target.value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setValue('work_addr_line1_block', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '屋苑/街道',
                  field: 'work_addr_line2',
                  value: confirmForm1?.work_addr_line2,
                  placeholder: '(E.g. HAPPY EST)',
                  onChange: (e) => {
                    e.target.value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase();
                    setValue('work_addr_line2', e.target.value);
                  },
                })}
                {renderMergedTextInput({
                  title: '公司電話',
                  field: 'work_tel_no2',
                  value: mergedWorkTelNo,
                  maxLength: 8,
                  inputMode: 'numeric',
                  placeholder: '(+852)',
                  validate: (value) => isOptionalHongKongPhone(value),
                  onChange: (e) => {
                    e.target.value = phoneNumberOnly(e?.target?.value).slice(0, 8);
                    setValue('work_tel_no2', e.target.value);
                  },
                })}
                {renderMergedDateInput({
                  title: '入職日期',
                  selectedDate: mergedJoinDate,
                  fields: { year: 'join_date3', month: 'join_date2', day: 'join_date1' },
                  required: false,
                  ageLimit: false,
                  limitToToday: true,
                })}
                {renderMergedSelect({
                  title: '每月出糧日',
                  field: 'pay_day',
                  value: mergedPayDay,
                  selectOptions: PAY_DAY_OPTIONS,
                })}
                {renderMergedSelect({
                  title: '住所持有人',
                  field: 'resi_own_sep',
                  value: mergedResiOwnSep,
                  selectOptions: RESI_OWNER_OPTIONS,
                })}
                {renderMergedMoneyInput({
                  title: '每月房租',
                  field: 'monthly_rent',
                  value: confirmForm1?.monthly_rent,
                  zeroAllow: true,
                  maxDigits: 6,
                })}
                {renderMergedSelect({
                  title: '同住人數',
                  field: 'number_living_with',
                  value: confirmForm1?.number_living_with,
                  selectOptions: options.number_living_with,
                })}
                {renderMergedDateInput({
                  title: '入住日期',
                  selectedDate: mergedMoveInDate,
                  fields: { year: 'move_in_date3', month: 'move_in_date2', day: 'move_in_date1' },
                  required: false,
                  ageLimit: false,
                })}
                {renderMergedTextInput({
                  title: '家用電話',
                  field: 'home_tel_no',
                  value: mergedHomeTelNo,
                  maxLength: 8,
                  inputMode: 'numeric',
                  placeholder: '(+852)',
                  validate: (value) => isOptionalHongKongPhone(value),
                  onChange: (e) => {
                    e.target.value = phoneNumberOnly(e?.target?.value).slice(0, 8);
                    setValue('home_tel_no', e.target.value);
                  },
                })}
              </>
            ) : (
              <>
                {renderValueRow('貸款目的', getOptionLabel(options.loan_purpose, confirmForm1?.loan_purpose))}
                {renderValueRow('學歷', getOptionLabel(options.education, confirmForm1?.education))}
                {renderValueRow('婚姻狀態', getOptionLabel(options.marriage_status, confirmForm1?.marriage_status))}
                {renderValueRow('電郵地址', confirmForm1?.email)}
                {renderValueRow('Facebook/Instagram', confirmForm1?.messenger_id)}
                {renderValueRow('公司名稱', mergedWorkName)}
                {hasMergedWorkAddressParts ? (
                  <>
                    <p className="text-gray mb-2 mt-2">公司地址</p>
                    {renderValueRow('區域', getOptionLabel(mergedAddressConfig?.area ?? options.area, mergedWorkArea))}
                    {renderValueRow('地區', getDistrictLabel(mergedWorkArea, mergedWorkDistrict, mergedAddressConfig))}
                    {renderValueRow('單位', confirmForm1?.work_addr_line1_room)}
                    {renderValueRow('樓層', confirmForm1?.work_addr_line1_floor)}
                    {renderValueRow('座數/大廈', confirmForm1?.work_addr_line1_block)}
                    {renderValueRow('屋苑/街道', confirmForm1?.work_addr_line2)}
                  </>
                ) : renderValueRow('公司地址', mergedWorkAddr)}
                {renderValueRow('公司電話', mergedWorkTelNo)}
                {renderValueRow('入職日期', formatDisplayDate(mergedJoinDate))}
                {renderValueRow('出糧日', mergedPayDay)}
                {renderValueRow('住所持有人', getOptionLabel(RESI_OWNER_OPTIONS, mergedResiOwnSep))}
                {renderValueRow('每月房租', hasValue(confirmForm1?.monthly_rent) ? `HK$ ${confirmForm1?.monthly_rent}` : '')}
                {renderValueRow('同住人數', getOptionLabel(options.number_living_with, confirmForm1?.number_living_with))}
                {renderValueRow('入住日期', formatDisplayDate(mergedMoveInDate))}
                {renderValueRow('家用電話', mergedHomeTelNo)}
              </>
            )}
          </div>
        ) : (
          <>
        {/* form 1 */}
        <div>
          <div>
            <div className="border-primary border-b text-left flex justify-start items-center text-primary text-xl py-1 my-4">
              <p className="mr-2">貸款資料</p>
              <img style={{ width: 23, height: 23 }} src={Edit} alt="" onClick={() => setIsEditingForm1(true)} className="cursor-pointer" />
            </div>
            <Common.EditInputMoney
              {...readOnlyAlignProps}
              setValue={setValue}
              register={register}
              errors={errors}
              title="貸款金額"
              setError={setError}
              value={location.state?.form1?.loan_amount}
              editing={isEditingForm1}
              field="loan_amount"
              placeholder=" "
              disabled={!!lendelaData?.offer?.loanAmount}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="貸款目的"
              value={location.state?.form1?.loan_purpose}
              editing={isEditingForm1}
              options={options.loan_purpose}
              field="loan_purpose"
              setValue={setValue}
              setError={setError}
              disabled={!!lendelaData?.loanPurpose}
            />
          </div>
          <div>
            <div className="border-primary border-b text-left flex justify-start items-center text-primary text-xl py-1 my-4">
              <p className="mr-2">申請人資料</p>
              <img style={{ width: 23, height: 23 }} src={Edit} alt="" onClick={() => setIsEditingForm11(true)} className="cursor-pointer" />
            </div>
            <div className="flex items-start">
              {!isEditingForm11 && <p className="text-gray w-1/2">英文姓名</p>}
              <div className='flex-1 min-w-0'>
                {!isEditingForm11 ? <p className={readOnlyValueClass}>{`${location.state?.form1?.first_name} ${location.state?.form1?.last_name}`}</p> :
                  <div className='w-full'>
                    <Common.EditInput
                      {...readOnlyAlignProps}
                      setValue={setValue}
                      register={register}
                      errors={errors}
                      title="姓氏"
                      value={location.state?.form1?.first_name}
                      editing={isEditingForm11}
                      field="first_name"
                      onChange={(e) => {
                        const value = alphabetOnly(e?.target?.value + '')
                        setValue('first_name', value.toUpperCase())
                      }}
                      disabled={!!lendelaData?.firstName}
                    />
                    <Common.EditInput
                      {...readOnlyAlignProps}
                      setValue={setValue}
                      register={register}
                      errors={errors}
                      title="名字"
                      value={location.state?.form1?.last_name}
                      editing={isEditingForm11}
                      field="last_name"
                      onChange={(e) => {
                        const value = alphabetOnly(e?.target?.value + '')
                        setValue('last_name', capitalizeName(value))
                      }}
                      disabled={!!lendelaData?.firstName}
                    />
                  </div>}
              </div>
            </div>
            <div className="flex items-start mt-2">
              {!isEditingForm11 && <p className="text-gray w-1/2">性別</p>}
              {!isEditingForm11 ? <p className={readOnlyValueClass}>{location.state?.form1?.cust_gender === '01' ? '先生' : '小姐'}</p> :
                <div className="w-full">
                  <div className="flex items-center my-3">
                    <p className="text-xl text-gray mb-2 w-1/2">性別</p>
                    <div className="w-1/2 border-b border-primary px-4">
                      <Select
                        placeholder="- 請選擇 -"
                        className="w-full"
                        theme={(theme) => ({
                          ...theme,
                          borderRadius: 0,
                        })}
                        styles={styles}
                        {...register('gender', { required: true })}
                        onChange={(e) => {
                          setValue('gender', e.value);
                          setGender(e.value)
                          setError('gender', null);
                        }}
                        defaultValue={options.genders[gender - 1]}
                        options={options.genders}
                        isSearchable={false}
                      />
                    </div>
                  </div>
                  {errors?.gender && errors?.gender?.type === 'required' && <span className="text-10 text-red-500">*必填</span>}
                </div>}
            </div>
            <div className="flex items-start mt-2">
              {isEditingForm11 ? <div className='w-full'>
                <p className="text-xl text-gray mb-2">香港身份證號碼</p>
                <div className="flex items-center">
                  <input disabled={!!lendelaData?.personalNumber} maxLength={2} {...register('hkid1', {
                    required: true,
                    onChange: (e) => {
                      setValue('hkid1', alphabetOnly(e.target.value.toUpperCase().replace(' ', '')))
                    }
                  })} className="px-2 outline-none text-primary w-1/3 text-center border-b border-primary" placeholder="A" />
                  <input disabled={!!lendelaData?.personalNumber}
                    maxLength={6}
                    {...register('hkid2', {
                      required: true,
                      onChange: (e) => {
                        setValue('hkid2', (e?.target?.value || '').replace(/[^0-9]/g, ''))
                      }
                    })}
                    className="px-2 outline-none text-primary text-center ml-2 mr-2 w-full border-b border-primary"
                    placeholder="123456"
                  />
                  <div className="border-b border-primary">
                    <span className="text-primary">(</span>
                    <input disabled={!!lendelaData?.personalNumber} maxLength={1} {...register('hkid3', {
                      required: true,
                      onChange: (e) => {
                        setValue('hkid3', alphabetAndNumberOnly(e.target.value.toUpperCase().replace(' ', '')))
                      }
                    })} className="px-2 outline-none text-primary w-3/4 text-center" placeholder="3" />
                    <span className="text-primary">)</span>
                  </div>
                </div>
                {!checkId(getValues('hkid1'), getValues('hkid2'), getValues('hkid3')) &&
                  (!(errors?.hkid1 || errors?.hkid2 || errors?.hkid3) ||
                    !(errors?.hkid1?.type === 'required' || errors?.hkid2?.type === 'required' || errors?.hkid3?.type === 'required')) && (
                    <span className="text-10 text-red-500">*請輸入正確的身份證號碼</span>
                  )}
              </div> : <>
                <p className="text-gray w-1/2">香港身份證號碼</p>
                <p className={readOnlyValueClass}>{`${_.dropRight(location.state?.form1?.hkid?.split(''))?.join('')}(${_.last(location.state?.form1?.hkid?.split(''))})`}</p>
              </>}
            </div>
            <div className="flex items-start  mt-2">
              {!isEditingForm11 ? <>
                <p className="text-gray w-1/2">出生日期</p>
                <p className={readOnlyValueClass}>{moment(location.state?.form1?.date_of_birth, 'YYYY/MM/DD').format('DD/MM/YYYY')}</p>
              </> : <div className='w-full'><p className="text-gray w-1/2">出生日期</p><Common.InputDate selectedDate={moment(location.state?.form1?.date_of_birth, 'YYYY/MM/DD').format('YYYY/MM/DD')} register={register} errors={errors} getValues={getValues} setValue={setValue} setError={setError} disabled={!!lendelaData?.dateOfBirth} /> </div>}
            </div>
            <div className="flex items-start  mt-2">
              <p className="text-gray w-1/2">手提電話</p>
              <p className={readOnlyValueClass}>{location.state?.form1?.phone_number}</p>
            </div>
            <div className="flex items-start  mt-2">
              {isEditingForm11 ?
                <div className='w-full'>
                  <Common.EditInput
                    {...readOnlyAlignProps}
                    setValue={setValue}
                    register={register}
                    errors={errors}
                    title="電郵地址"
                    value={location.state?.form1?.email}
                    editing={isEditingForm11}
                    field="email"
                    pattern={OPTIONAL_EMAIL_PATTERN}
                    patternErrorMessage="*請輸入正確的電郵"
                    onChange={(e) => {
                      e.target.value = emailAddressOnly(e.target?.value)
                      setValue('email', e.target.value, { shouldValidate: true })
                    }}
                    disabled={!!lendelaData?.email}
                  />
                </div> :
                <>
                  <p className="text-gray w-1/2">電郵地址</p>
                  <p className={readOnlyValueClass}>{location.state?.form1?.email}</p>
                </>
              }
            </div>
            <div className="flex items-start  mt-2">
              {!isEditingForm11 ? <>
                <p className="text-gray w-1/2">學歷</p>
                <p className={readOnlyValueClass}>{options.education?.find((i) => i?.value === location.state?.form1?.education)?.label}</p></>
                : <div className='w-full'>
                  <Common.EditSelect
                    {...readOnlyAlignProps}
                    register={register}
                    errors={errors}
                    title="學歷"
                    value={location.state?.form1?.education}
                    editing={isEditingForm11}
                    options={options.education}
                    field="education"
                    setValue={setValue}
                    setError={setError}
                  /></div>}
            </div>
            <div className="flex items-start  mt-2">
              {!isEditingForm11 ?
                <>              <p className="text-gray w-1/2">婚姻狀態</p>
                  <p className={readOnlyValueClass}>{options.marriage_status?.find((i) => i?.value === location.state?.form1?.marriage_status)?.label}</p>
                </> : <div className='w-full'>
                  <Common.EditSelect
                    {...readOnlyAlignProps}
                    register={register}
                    errors={errors}
                    title="婚姻狀態"
                    value={location.state?.form1?.marriage_status}
                    editing={isEditingForm11}
                    options={options.marriage_status}
                    field="marriage_status"
                    setValue={setValue}
                    setError={setError}
                  /></div>}
            </div>
            {isEditingForm11 && (
              <div className="flex items-start mt-2">
                <div className="w-1/2">
                  <p className="text-gray">推薦碼 (如有)</p>
                  {errors?.ref_recommender_no?.type === 'validate' && <span className="text-10 text-red-500">推薦碼必須為6個字元</span>}
                </div>
                <div className="flex-1">
                  <input
                    defaultValue={location.state?.form1?.ref_recommender_no ?? ''}
                    maxLength={6}
                    {...refRecommenderNoField}
                    onChange={(e) => {
                      e.target.value = normalizeRefRecommenderNo(e.target.value);
                      refRecommenderNoField.onChange(e);
                    }}
                    className="w-full pr-2 outline-none text-primary border-primary border-b"
                    style={{ height: 36 }}
                  />
                </div>
              </div>
            )}
            {!isEditingForm11 && !!location.state?.form1?.ref_recommender_no?.trim() && (
              <div className="flex items-start mt-2">
                <p className="text-gray w-1/2">推薦碼 (如有)</p>
                <p className={readOnlyValueClass}>{location.state?.form1?.ref_recommender_no}</p>
              </div>
            )}
            {isForm1MergedFlow && !isEditingForm11 && renderValueRow('Facebook/Instagram', confirmForm1?.messenger_id)}
            {isForm1MergedFlow && isEditingForm11 && (
              <div className="flex items-start mt-2">
                <p className="text-gray w-1/2">Facebook/Instagram</p>
                <input
                  defaultValue={confirmForm1?.messenger_id ?? ''}
                  maxLength={50}
                  {...register('messenger_id', {
                    onChange: (e) => {
                      e.target.value = (e?.target?.value || '').slice(0, 50);
                      setValue('messenger_id', e.target.value);
                    },
                  })}
                  className="flex-1 pr-2 outline-none text-primary border-primary border-b"
                  style={{ height: 36 }}
                />
              </div>
            )}
          </div>
        </div>
        {/* form 2 */}
        <div>
          <div>
            <div className="border-primary border-b text-left flex justify-start items-center text-primary text-xl py-1 my-4">
              <p className="mr-2">工作資料</p>
              {!isForm1MergedFlow && <img style={{ width: 23, height: 23 }} src={Edit} alt="" onClick={() => setIsEditingForm2(true)} className="cursor-pointer" />}
            </div>
            {isForm1MergedFlow ? (
              <>
                {renderValueRow('公司名稱', mergedWorkName)}
                {renderValueRow('公司地址', mergedWorkAddr)}
                {renderValueRow('公司電話', mergedWorkTelNo)}
                {renderValueRow('入職日期', formatDisplayDate(mergedJoinDate))}
                {renderValueRow('出糧日', mergedPayDay)}
              </>
            ) : (
              <>
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="業務類型"
              value={location.state?.form2?.business_type}
              editing={isEditingForm2}
              options={options.business_type}
              field="business_type"
              setValue={setValue}
              setError={setError}
              disabled={!!lendelaData?.jobIndustry}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="工作類型"
              value={location.state?.form2?.job_type}
              editing={isEditingForm2}
              options={options.job_type}
              field="job_type"
              setValue={setValue}
              setError={setError}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="職位"
              value={location.state?.form2?.position}
              editing={isEditingForm2}
              options={options.position}
              field="position"
              setValue={setValue}
              setError={setError}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="受聘形式"
              value={location.state?.form2?.employment_type}
              editing={isEditingForm2}
              options={options.employment_type}
              field="employment_type"
              setValue={setValue}
              setError={setError}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="出糧方式"
              value={location.state?.form2?.receipt_way}
              editing={isEditingForm2}
              options={options.receipt_way}
              field="receipt_way"
              setValue={setValue}
              setError={setError}
              disabled={!!lendelaData?.payrollType}
            />
            <Common.EditInputMoney
              {...readOnlyAlignProps}
              setValue={setValue}
              register={register}
              errors={errors}
              title="月收入"
              setError={setError}
              value={location.state?.form2?.monthly_income}
              editing={isEditingForm2}
              field="monthly_income"
              placeholder=" "
              zeroAllow={true}
              disabled={!!lendelaData?.monthlyIncome}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="支薪方式"
              value={location.state?.form2?.payroll_method}
              editing={isEditingForm2}
              options={options.payroll_method}
              field="payroll_method"
              setValue={setValue}
              setError={setError}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="MPF強積金"
              value={location.state?.form2?.mpf}
              editing={isEditingForm2}
              options={options.mpf}
              field="mpf"
              setValue={setValue}
              setError={setError}
            />
              </>
            )}
          </div>
        </div>
        {/* form 3 */}
        <div>
          <div>
            <div className="border-primary border-b text-left flex justify-start items-center text-primary text-xl py-1 my-4">
              <p className="mr-2">住址資料</p>
              {!isForm1MergedFlow && <img style={{ width: 23, height: 23 }} src={Edit} alt="" onClick={() => setIsEditingForm3(true)} className="cursor-pointer" />}
            </div>
            {isForm1MergedFlow ? (
              <>
                <p className="text-gray mb-2">住宅地址</p>
                {renderValueRow('區域', getOptionLabel(mergedAddressConfig?.area ?? options.area, mergedArea))}
                {renderValueRow('地區', getDistrictLabel(mergedArea, mergedDistrict, mergedAddressConfig))}
                {renderValueRow('單位/樓層/座數/大廈', confirmForm1?.addr_line1)}
                {renderValueRow('屋苑/街道', confirmForm1?.addr_line2)}
                {renderValueRow('住所持有人', getOptionLabel(RESI_OWNER_OPTIONS, mergedResiOwnSep))}
                {renderValueRow('每月房租', hasValue(confirmForm1?.monthly_rent) ? `HK$ ${confirmForm1?.monthly_rent}` : '')}
                {renderValueRow('同住人數', getOptionLabel(options.number_living_with, confirmForm1?.number_living_with))}
                {renderValueRow('入住日期', formatDisplayDate(mergedMoveInDate))}
                {renderValueRow('家用電話', mergedHomeTelNo)}
              </>
            ) : (
              <>
            <p className="text-gray mb-2">住宅地址</p>
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="區域"
              value={getValues('area') || location.state?.form3?.area}
              editing={isEditingForm3}
              options={options?.area}
              field="area"
              setValue={setValue}
              setError={setError}
              onChange={(e) => {
                const address_config = location.state?.form3?.address_config;
                switch (e.value) {
                  case 'HK':
                    setOptionsDistrict(address_config?.district_hk);
                    break;
                  case 'KLN':
                    setOptionsDistrict(address_config?.district_kw);
                    break;
                  case 'NT':
                    setOptionsDistrict(address_config?.district_nt);
                    break;
                  default:
                    break;
                }
                setValue('area', e.value)
                setValue('district', null)
              }}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="地區"
              value={getValues('district')}
              editing={isEditingForm3}
              options={optionsDistrict}
              field="district"
              setValue={setValue}
              setError={setError}
              valueEnabled={true}
            />
            <Common.EditInput
              {...readOnlyAlignProps}
              setValue={setValue}
              register={register}
              errors={errors}
              title="單位/樓層/座數/大廈"
              value={location.state?.form3?.addr_line1}
              editing={isEditingForm3}
              field="building"
              onChange={(e) => {
                const value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase()
                setValue('building', value)
              }}
            />
            <Common.EditInput
              {...readOnlyAlignProps}
              setValue={setValue}
              register={register}
              errors={errors}
              title="屋苑/街道"
              value={location.state?.form3?.addr_line2}
              editing={isEditingForm3}
              field="estate"
              onChange={(e) => {
                const value = alphabetAndNumberOnly((e?.target?.value || '')).toUpperCase()
                setValue('estate', value)
              }}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="房屋類型"
              value={location.state?.form3?.property_type}
              editing={isEditingForm3}
              options={options.property_type}
              field="property_type"
              setValue={setValue}
              setError={setError}
              disabled={!!lendelaData?.residentialPropertyType}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="同住人數"
              value={location.state?.form3?.number_living_with}
              editing={isEditingForm3}
              options={options.number_living_with}
              field="number_living_with"
              setValue={setValue}
              setError={setError}
            />
            <Common.EditSelect
              {...readOnlyAlignProps}
              register={register}
              errors={errors}
              title="持有人"
              value={location.state?.form3?.property_owner}
              editing={isEditingForm3}
              options={options.property_owner}
              field="property_owner"
              setValue={setValue}
              setError={setError}
              disabled={!!lendelaData?.residentialStatus}
            />
            <Common.EditInputMoney
              {...readOnlyAlignProps}
              setValue={setValue}
              register={register}
              errors={errors}
              title="每月房租"
              setError={setError}
              value={location.state?.form3?.monthly_rent}
              editing={isEditingForm3}
              field="monthly_rent"
              placeholder=' '
              zeroAllow={true}
            />
              </>
            )}
          </div>
        </div>
          </>
        )}
        <Common.FormActionRow
          onBack={onClickPrevious}
          nextLabel="確認並提交"
          disabled={isSubmitting}
          loading={isSubmitting}
        />
        <div className="hidden">
          <button disabled={isSubmitting} type="submit" className={`rounded-full text-white text-xl px-14 py-3 ${isSubmitting ? 'bg-mercury' : 'bg-primary'}`}>
            確認資料無誤
          </button>
        </div>
      </div>
    </form>
  );
}

export default ApplicationConfirmData;
